import Vue from 'vue'
import Router from 'vue-router'
import { permissionsMixin } from 'common-mixins/permissions'
const searchReadPermissionByPath =
  permissionsMixin.methods.searchReadPermissionByPath
Vue.use(Router)

export const createRouter = (kuzzle, store) => {
  const hasPermission = (pathName) => {
    const findReadPermission = searchReadPermissionByPath(
      store.getters['auth/currentPermissions'],
      pathName,
    )
    return findReadPermission === null ? false : findReadPermission.value
  }

  const adminGuard = async (_to, _from, next) => {
    if (store.getters['auth/currentProfile'] === 'admin') {
      next()
    } else {
      next({ name: 'notFound' })
    }
  }

  const authenticationGuard = async (to, _from, next) => {
    try {
      if (await store.dispatch('auth/CHECK_TOKEN', kuzzle)) {
        if (!store.getters['auth/currentDefaultPage']) {
          next()
        }

        if (!hasPermission(to.name) && to.name !== 'redirect-qr') {
          const pageName = store.getters['auth/currentDefaultPage']
          next({ name: pageName })
        }

        next()
      } else {
        next({ name: 'login', query: { to: to.name, params: to.params } })
      }
    } catch (error) {
      console.error(error.message)
      next({ name: 'login', query: { to: to.name, params: to.params } })
    }
  }

  const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
      {
        path: '/login',
        name: 'login',
        component: () => import('../../common/views/SilentCheckSSO.vue'),
      },
      {
        path: '/',
        beforeEnter: authenticationGuard,
        component: () => import('@/components/DefaultContainer.vue'),
        children: [
          {
            path: '',
            name: 'homePage',
            component: () => import('@/views/HomePage.vue'),
          },
        ],
      },
      {
        path: '/asset',
        beforeEnter: authenticationGuard,
        component: () => import('@/components/DefaultContainer.vue'),
        children: [
          {
            path: ':customerName?',
            name: 'asset',
            component: () => import('@/views/Tracking.vue'),
          },
          {
            path: ':customerName/view/:assetId',
            name: 'asset-view',
            component: () => import('@/components/asset/AssetView.vue'),
          },
        ],
      },
      {
        path: '/live-site',
        beforeEnter: authenticationGuard,
        component: () => import('@/components/DefaultContainer.vue'),
        children: [
          {
            path: 'safety-dashboard',
            name: 'safetyDashboard',
            component: () => import('@/views/SafetyDashboard.vue'),
          },
          {
            path: 'data-quality',
            name: 'dataQuality',
            component: () => import('@/views/DataQuality.vue'),
          },
        ],
      },
      {
        path: '/dashboard',
        beforeEnter: authenticationGuard,
        component: () => import('@/components/DefaultContainer.vue'),
        children: [
          {
            path: 'overview',
            name: 'overview',
            component: () => import('@/views/Dashboard.vue'),
          },
          {
            path: 'bi-dashboards',
            name: 'bi-dashboards',
            component: () => import('@/views/BiDashboards.vue'),
          },
          {
            path: 'counters',
            name: 'counters',
            component: () => import('@/views/counters/Counters.vue'),
          },
          {
            path: 'counters/:counterId',
            name: 'countersAnchor',
            component: () => import('@/views/counters/Counters.vue'),
          },
          {
            path: 'tag-in-tag-out',
            name: 'tag-in-tag-out',
            component: () => import('@/views/TagInTagOut'),
          },
        ],
      },
      {
        path: '/alerts',
        beforeEnter: authenticationGuard,
        component: () => import('@/components/DefaultContainer.vue'),
        children: [
          {
            path: 'list',
            name: 'alerts_list',
            beforeEnter: authenticationGuard,
            component: () => import('@/views/Alerts.vue'),
          },
          {
            path: 'configuration',
            name: 'alerts_configuration',
            beforeEnter: authenticationGuard,
            component: () => import('@/views/AlertConfigs.vue'),
          },
        ],
      },
      {
        path: '/administration',
        beforeEnter: authenticationGuard,
        component: () => import('@/components/DefaultContainer.vue'),
        children: [
          {
            path: 'resources',
            name: 'resources',
            beforeEnter: adminGuard,
            component: () => import('@/views/AdministrationResources.vue'),
          },
          {
            path: 'tags/:manufacturerId?',
            name: 'tags',
            beforeEnter: adminGuard,
            component: () => import('@/views/AdministrationTags.vue'),
          },
          {
            path: 'resources/create',
            name: 'resources_select_family',
            beforeEnter: adminGuard,
            component: () => import('@/views/AdministrationFamilySelect.vue'),
          },
          {
            path: 'resources/create/asset/:family/:subFamily',
            name: 'resources_form',
            beforeEnter: adminGuard,
            component: () => import('@/views/AdministrationCreateAsset.vue'),
          },
          {
            path: 'reports',
            name: 'reports',
            beforeEnter: adminGuard,
            component: () =>
              import('@/views/administration/reports/ReportsList.vue'),
          },
          {
            path: 'reports/create',
            name: 'reportsFamilySelect',
            beforeEnter: adminGuard,
            component: () =>
              import('@/views/administration/reports/ReportsFamilySelect.vue'),
          },
          {
            path: 'reports/create/:family/:subFamily',
            name: 'reports_form',
            beforeEnter: adminGuard,
            component: () =>
              import('@/views/administration/reports/ReportsCreate.vue'),
          },
          {
            path: 'labels',
            name: 'labels',
            beforeEnter: adminGuard,
            component: () => import('@/views/administration/Labels.vue'),
          },
          {
            path: 'sites',
            name: 'sites',
            beforeEnter: adminGuard,
            component: () => import('@/views/administration/Sites.vue'),
          },
          {
            path: 'counters',
            name: 'counters-admin-view',
            beforeEnter: adminGuard,
            component: () => import('@/views/counters/CountersAdminView.vue'),
          },
          {
            path: 'counters/create',
            name: 'counter-admin-create',
            beforeEnter: adminGuard,
            component: () => import('@/views/counters/CreateCounterView.vue'),
          },
        ],
      },
      {
        path: '/:clientId/qr-code/:qrCodeId',
        beforeEnter: authenticationGuard,
        name: 'redirect-qr',
        component: () => import('@/components/QrCodeRedirect.vue'),
      },
      {
        path: '/debug-ui',
        beforeEnter: authenticationGuard,
        component: () => import('@/components/ui/DebugUi'),
      },
      {
        path: '/experimental-features',
        beforeEnter: authenticationGuard,
        component: () => import('@/components/DefaultContainer.vue'),
        children: [
          {
            path: '',
            name: 'experimental-features',
            component: () => import('../../common/views/FeatureFlagsView.vue'),
          },
        ],
      },
      {
        path: '*',
        name: 'notFound',
        beforeEnter: authenticationGuard,
        component: () => import('../../common/views/404.vue'),
      },
    ],
  })

  router.beforeEach(async (_to, _from, next) => {
    /**
     * This route guard ensures that the app is connected to Kuzzle before
     * passing to the next route.
     */
    const offlinePageDelay = 1500
    return new Promise(async (resolve) => {
      setTimeout(() => {
        if (!store.state.app.online) {
          store.commit('app/SET_WAITING_FOR_CONNECTION')
        }
      }, offlinePageDelay)
      const onceConnected = () => {
        store.commit('app/SET_ONLINE')

        setTimeout(() => {
          store.commit('app/UNSET_WAITING_FOR_CONNECTION')
        }, offlinePageDelay)

        kuzzle.removeListener('connected', onceConnected)
        kuzzle.removeListener('reconnected', onceConnected)

        next()
        return resolve()
      }
      if (!store.state.app.online) {
        kuzzle.addListener('connected', onceConnected)
        kuzzle.addListener('reconnected', onceConnected)
      } else {
        onceConnected()
      }
    })
  })

  return router
}
